import React, { useState, useEffect } from 'react'
import { Container, Row, Col, Card, CardBody, CardHeader } from 'reactstrap'
import { Breadcrumbs, H4 } from '../../AbstractElements'
import { useAuth } from '../../_helper/Auth/AuthProvider'
import { useLayout } from '../../Hooks/useLayout'
import { Link } from 'react-router-dom'
import { getFile } from '../../lib/file'
import { getIdData } from '../../lib/studentinfo'

const StudentProfile = () => {
	const { user } = useAuth()
	const layout = useLayout()
	const [studentInfo, setStudentInfo] = useState(null)
	const [avatar, setAvatar] = useState('')
	const [loading, setLoading] = useState(true)

	useEffect(() => {
		const fetchProfileData = async () => {
			if (!user) return

			try {
				const info = await getIdData(user.id)
				if (info && info.length > 0) {
					setStudentInfo(info[0])

					// Fetch avatar if available
					if (info[0].avatar_url) {
						try {
							const avatarData = await getFile(info[0].avatar_url)
							setAvatar(avatarData.publicUrl)
						} catch (error) {
							console.error('Error fetching avatar:', error)
						}
					}
				}
			} catch (error) {
				console.error('Error fetching profile data:', error)
			} finally {
				setLoading(false)
			}
		}

		fetchProfileData()
	}, [user])

	if (!user) {
		return (
			<>
				<Breadcrumbs mainTitle="Student Profile" parent="Profile" title="Student Profile Details" />
				<Container fluid={true}>
					<Row>
						<Col sm="12">
							<Card>
								<CardBody>
									<div className="text-center">
										<div className="alert alert-warning" role="alert">
											Please log in to view your profile.
										</div>
									</div>
								</CardBody>
							</Card>
						</Col>
					</Row>
				</Container>
			</>
		)
	}

	if (loading) {
		return (
			<>
				<Breadcrumbs mainTitle="Student Profile" parent="Profile" title="Student Profile Details" />
				<Container fluid={true}>
					<Row>
						<Col sm="12">
							<Card>
								<CardBody>
									<div className="text-center">
										<div className="spinner-border text-primary" role="status">
											<span className="sr-only">Loading...</span>
										</div>
									</div>
								</CardBody>
							</Card>
						</Col>
					</Row>
				</Container>
			</>
		)
	}

	return (
		<>
			<Breadcrumbs mainTitle="Student Profile" parent="Profile" title="Student Profile Details" />
			<Container fluid={true}>
				<Row>
					<Col sm="12">
						<Card>
							<CardHeader className="bg-success text-white">
								<H4 attrH4={{ className: 'card-title mb-2 text-white' }}>
									<i className="fa fa-graduation-cap me-2"></i>
									Student Profile Details
								</H4>
								<p className="mb-0 text-white-50">View your academic information and personal details.</p>
							</CardHeader>
							<CardBody>
								{/* Avatar Section */}
								<div className="course-group mb-4 p-4 border rounded" style={{ backgroundColor: '#f8f9fa' }}>
									<div className="d-flex align-items-center">
										<div className="position-relative">
											<img
												src={avatar || require('../../assets/images/user/user-dp.png')}
												alt="Profile Avatar"
												className="img-fluid rounded-circle border border-3 border-white shadow"
												style={{
													width: '120px',
													height: '120px',
													objectFit: 'cover',
													transition: 'all 0.3s ease'
												}}
											/>
										</div>
										<div className="ms-4">
											<h4 className="mb-2 text-success">Profile Picture</h4>
											<p className="mb-1 text-muted">Your current profile picture</p>
											<small className="text-muted">
												<i className="fa fa-info-circle me-1"></i>
												PNG or JPG, max 800x800 pixels
											</small>
										</div>
									</div>
								</div>

								{/* Student Information */}
								<div className="settings-widget">
									<div className="settings-inner-blk p-0">
										<div className="checkout-form personal-address add-course-info">
											<div className="personal-info-head mb-4">
												<h4 className="text-success">
													<i className="fa fa-user me-2"></i>
													Student Details
												</h4>
												<p className="text-muted mb-0">Your academic information and personal details</p>
											</div>

											{/* Personal Information Section */}
											<div className="row mb-4">
												<div className="col-12">
													<h5 className="text-primary mb-3">
														<i className="fa fa-user-circle me-2"></i>
														Personal Information
													</h5>
												</div>
												<div className="col-lg-6">
													<div className="form-group mb-3">
														<label className="form-control-label fw-bold text-muted mb-2">First Name</label>
														<div className="p-3 border rounded bg-white">
															<p className="mb-0 fw-semibold">{studentInfo?.name || 'Not specified'}</p>
														</div>
													</div>
												</div>
												<div className="col-lg-6">
													<div className="form-group mb-3">
														<label className="form-control-label fw-bold text-muted mb-2">Last Name</label>
														<div className="p-3 border rounded bg-white">
															<p className="mb-0 fw-semibold">{studentInfo?.surname || 'Not specified'}</p>
														</div>
													</div>
												</div>
												<div className="col-lg-6">
													<div className="form-group mb-3">
														<label className="form-control-label fw-bold text-muted mb-2">Phone</label>
														<div className="p-3 border rounded bg-white">
															<p className="mb-0 fw-semibold">{studentInfo?.phone || 'Not specified'}</p>
														</div>
													</div>
												</div>
												<div className="col-lg-6">
													<div className="form-group mb-3">
														<label className="form-control-label fw-bold text-muted mb-2">Email</label>
														<div className="p-3 border rounded bg-white">
															<p className="mb-0 fw-semibold">{studentInfo?.email || 'Not specified'}</p>
														</div>
													</div>
												</div>
												<div className="col-lg-6">
													<div className="form-group mb-3">
														<label className="form-control-label fw-bold text-muted mb-2">Nick Name</label>
														<div className="p-3 border rounded bg-white">
															<p className="mb-0 fw-semibold">{studentInfo?.username || 'Not specified'}</p>
														</div>
													</div>
												</div>
												<div className="col-lg-6">
													<div className="form-group mb-3">
														<label className="form-control-label fw-bold text-muted mb-2">Grade Level</label>
														<div className="p-3 border rounded bg-white">
															<p className="mb-0 fw-semibold">{studentInfo?.class || 'Not specified'}</p>
														</div>
													</div>
												</div>
												<div className="col-lg-6">
													<div className="form-group mb-3">
														<label className="form-control-label fw-bold text-muted mb-2">School Name</label>
														<div className="p-3 border rounded bg-white">
															<p className="mb-0 fw-semibold">{studentInfo?.schoolname || 'Not specified'}</p>
														</div>
													</div>
												</div>
												<div className="col-lg-6">
													<div className="form-group mb-3">
														<label className="form-control-label fw-bold text-muted mb-2">Graduation Year</label>
														<div className="p-3 border rounded bg-white">
															<p className="mb-0 fw-semibold">{studentInfo?.graduateyear || 'Not specified'}</p>
														</div>
													</div>
												</div>
											</div>

											{/* Academic Information Section */}
											<div className="row mb-4">
												<div className="col-12">
													<h5 className="text-primary mb-3">
														<i className="fa fa-graduation-cap me-2"></i>
														Academic Information
													</h5>
												</div>
												<div className="col-lg-6">
													<div className="form-group mb-3">
														<label className="form-control-label fw-bold text-muted mb-2">GPA</label>
														<div className="p-3 border rounded bg-white">
															<p className="mb-0 fw-semibold">{studentInfo?.gpa || 'Not specified'}</p>
														</div>
													</div>
												</div>
												<div className="col-lg-6">
													<div className="form-group mb-3">
														<label className="form-control-label fw-bold text-muted mb-2">SAT/PSAT Scores</label>
														<div className="p-3 border rounded bg-white">
															<p className="mb-0 fw-semibold">{studentInfo?.satpsat || 'Not specified'}</p>
														</div>
													</div>
												</div>
												<div className="col-lg-6">
													<div className="form-group mb-3">
														<label className="form-control-label fw-bold text-muted mb-2">Practice SAT</label>
														<div className="p-3 border rounded bg-white">
															<p className="mb-0 fw-semibold">{studentInfo?.statetest || 'Not specified'}</p>
														</div>
													</div>
												</div>
												<div className="col-lg-6">
													<div className="form-group mb-3">
														<label className="form-control-label fw-bold text-muted mb-2">Major</label>
														<div className="p-3 border rounded bg-white">
															<p className="mb-0 fw-semibold">{studentInfo?.major || 'Not specified'}</p>
														</div>
													</div>
												</div>
												<div className="col-lg-6">
													<div className="form-group mb-3">
														<label className="form-control-label fw-bold text-muted mb-2">Career</label>
														<div className="p-3 border rounded bg-white">
															<p className="mb-0 fw-semibold">{studentInfo?.career || 'Not specified'}</p>
														</div>
													</div>
												</div>
												<div className="col-lg-6">
													<div className="form-group mb-3">
														<label className="form-control-label fw-bold text-muted mb-2">Five Adjectives</label>
														<div className="p-3 border rounded bg-white">
															<p className="mb-0 fw-semibold">{studentInfo?.fiveAdjectives || 'Not specified'}</p>
														</div>
													</div>
												</div>
											</div>

											{/* Additional Information Section */}
											<div className="row mb-4">
												<div className="col-12">
													<h5 className="text-primary mb-3">
														<i className="fa fa-info-circle me-2"></i>
														Additional Information
													</h5>
												</div>
												<div className="col-12">
													<div className="form-group mb-3">
														<label className="form-control-label fw-bold text-muted mb-2">High School Statistics</label>
														<div className="p-3 border rounded bg-white">
															<p className="mb-0 fw-semibold">{studentInfo?.hsSchoolStatistics || 'Not specified'}</p>
														</div>
													</div>
												</div>
												<div className="col-12">
													<div className="form-group mb-3">
														<label className="form-control-label fw-bold text-muted mb-2">Top Three Colleges</label>
														<div className="p-3 border rounded bg-white">
															<p className="mb-0 fw-semibold">{studentInfo?.topThreeColleges || 'Not specified'}</p>
														</div>
													</div>
												</div>
												<div className="col-12">
													<div className="form-group mb-3">
														<label className="form-control-label fw-bold text-muted mb-2">Letter of Recommendation</label>
														<div className="p-3 border rounded bg-white">
															<p className="mb-0 fw-semibold">{studentInfo?.lor || 'Not specified'}</p>
														</div>
													</div>
												</div>
												<div className="col-12">
													<div className="form-group mb-3">
														<label className="form-control-label fw-bold text-muted mb-2">Activities</label>
														<div className="p-3 border rounded bg-white">
															<p className="mb-0 fw-semibold">{studentInfo?.activities || 'Not specified'}</p>
														</div>
													</div>
												</div>
												<div className="col-12">
													<div className="form-group mb-3">
														<label className="form-control-label fw-bold text-muted mb-2">College Essay Progress</label>
														<div className="p-3 border rounded bg-white">
															<p className="mb-0 fw-semibold">{studentInfo?.collegeEssay || 'Not specified'}</p>
														</div>
													</div>
												</div>
												<div className="col-12">
													<div className="form-group mb-3">
														<label className="form-control-label fw-bold text-muted mb-2">Counselor's Comment</label>
														<div className="p-3 border rounded bg-white">
															<p className="mb-0 fw-semibold">{studentInfo?.instructorNote || 'Not specified'}</p>
														</div>
													</div>
												</div>
												<div className="col-12">
													<div className="form-group mb-3">
														<label className="form-control-label fw-bold text-muted mb-2">Student's Comment</label>
														<div className="p-3 border rounded bg-white">
															<p className="mb-0 fw-semibold">{studentInfo?.studentNote || 'Not specified'}</p>
														</div>
													</div>
												</div>
											</div>

											{/* Action Buttons */}
											<div className="row">
												<div className="col-12">
													<div className="update-profile d-flex flex-column flex-sm-row gap-3 justify-content-between mt-4">
														<Link
															to={`/${user.role.toLowerCase()}/profile-edit/${layout}`}
															className="btn btn-success btn-lg px-4 py-2 text-white"
															style={{ minWidth: '140px' }}
														>
															<i className="fa fa-edit me-2"></i>
															Update Profile
														</Link>
														<Link
															to={`/${user.role.toLowerCase()}/change-password/${layout}`}
															className="btn btn-warning btn-lg px-4 py-2 text-white"
															style={{ minWidth: '160px' }}
														>
															<i className="fa fa-key me-2"></i>
															Change Password
														</Link>
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>
							</CardBody>
						</Card>
					</Col>
				</Row>
			</Container>
		</>
	)
}

export { StudentProfile }
